import type { SectionProps } from "../context";
import { messageVars, type RenderContext } from "../context";
import { imageSrc, srcSet, telHref, waLink } from "../helpers";
import { Icon } from "../icons";
import type { NavLink } from "../nav";
import { Container, SectionShell, SiteLink, cx } from "../ui";

function Brand({ ctx, href }: { ctx: RenderContext; href: string }) {
  const logo = ctx.site.logo;
  const src = logo ? imageSrc(logo, 480) : null;
  return (
    <SiteLink ctx={ctx} href={href} className="flex min-h-11 min-w-0 items-center gap-3" ariaLabel={`${ctx.site.name} – beranda`}>
      {logo && src ? (
        <img
          src={src}
          srcSet={srcSet(logo)}
          sizes="200px"
          alt={logo.alt && logo.alt !== ctx.site.name ? logo.alt : ""}
          width={logo.width}
          height={logo.height}
          loading="eager"
          decoding="async"
          className="h-9 w-auto max-w-[7.5rem] object-contain @3xl:h-10"
        />
      ) : null}
      <span className="baze-heading truncate py-1 text-xl leading-tight @3xl:text-[1.375rem]">{ctx.site.name}</span>
    </SiteLink>
  );
}

function NavLinks({ ctx, links, className, linkClassName }: { ctx: RenderContext; links: NavLink[]; className?: string; linkClassName?: string }) {
  if (links.length === 0) return null;
  return (
    <ul className={className}>
      {links.map((l) => (
        <li key={l.href}>
          <SiteLink ctx={ctx} href={l.href} className={cx(linkClassName, l.current && "text-site-text")}>
            <span aria-current={l.current ? "page" : undefined}>{l.label}</span>
          </SiteLink>
        </li>
      ))}
    </ul>
  );
}

function navCta(ctx: RenderContext, label: string) {
  const wa = waLink(ctx.site.contact.whatsapp, ctx.site.whatsapp?.defaultMessage, messageVars(ctx));
  if (wa) return { href: wa, whatsapp: true, label };
  if (ctx.anchors.has("kontak")) return { href: "#kontak", whatsapp: false, label };
  const tel = telHref(ctx.site.contact.phone);
  return tel ? { href: tel, whatsapp: false, label } : null;
}

function MobileMenu({ ctx, links, cta, floating }: { ctx: RenderContext; links: NavLink[]; cta: ReturnType<typeof navCta>; floating?: boolean }) {
  if (links.length === 0) return null;
  return (
    <details className="group @3xl:hidden">
      <summary className="grid size-11 cursor-pointer list-none place-items-center rounded-full border border-site-border text-site-text transition hover:bg-site-text/5 [&::-webkit-details-marker]:hidden">
        <span className="sr-only">Buka menu</span>
        <Icon name="menu" className="size-5 group-open:hidden" />
        <Icon name="close" className="hidden size-5 group-open:block" />
      </summary>
      {/* hidden while closed: Chrome lays out closed <details> content in a contained box, where this
          panel collapses to the button's width and its text widens the page (horizontal scroll). */}
      <div className={cx("absolute inset-x-0 top-full hidden border-site-border bg-site-bg shadow-site-md group-open:block", floating ? "mt-2 rounded-site-lg border" : "border-b")}>
        <nav aria-label="Navigasi seluler" className="px-5 pt-2 pb-5">
          <NavLinks
            ctx={ctx}
            links={links}
            className="divide-y divide-site-border"
            linkClassName="flex min-h-12 items-center justify-between text-base font-medium text-site-text"
          />
          {cta ? (
            <SiteLink
              ctx={ctx}
              href={cta.href}
              whatsapp={cta.whatsapp}
              className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-site-button bg-site-primary px-5 font-semibold text-site-on-primary"
            >
              {cta.whatsapp ? <Icon name="whatsapp" className="size-[1.125rem]" /> : null}
              {cta.label}
            </SiteLink>
          ) : null}
        </nav>
      </div>
    </details>
  );
}

function CtaPill({ ctx, cta, className }: { ctx: RenderContext; cta: NonNullable<ReturnType<typeof navCta>>; className?: string }) {
  return (
    <SiteLink
      ctx={ctx}
      href={cta.href}
      whatsapp={cta.whatsapp}
      fieldPath="content.ctaLabel"
      className={cx(
        "inline-flex min-h-11 items-center gap-2 rounded-site-button bg-site-primary px-4 text-sm font-semibold whitespace-nowrap text-site-on-primary shadow-site-sm transition hover:shadow-site-md @3xl:px-5",
        className,
      )}
    >
      {cta.whatsapp ? <Icon name="whatsapp" className="size-4" /> : null}
      {cta.label}
    </SiteLink>
  );
}

/** Thin reading-progress bar along the bottom edge (scroll-driven CSS; hidden where unsupported). */
const Progress = () => <span aria-hidden="true" className="baze-progress pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-site-primary" />;

export function Navbar({ node, ctx }: SectionProps<"navbar">) {
  const c = node.content;
  const cta = c.showCta === false ? null : navCta(ctx, c.ctaLabel?.trim() || "Hubungi kami");
  const home = ctx.definition.pages.length > 1 ? "/" : `#${node.anchor}`;
  const links = ctx.nav;
  const desktopLink = "inline-flex min-h-11 items-center px-3 text-[0.9375rem] font-medium text-site-muted transition-colors hover:text-site-text";

  if (node.variant === "floating") {
    return (
      <SectionShell ctx={ctx} node={node} as="header" padded={false} className="baze-navbar sticky top-(--baze-sticky-top,0px) z-40 bg-transparent! px-3 pt-3 [animation:none]! @3xl:px-6 @3xl:pt-4">
        <div className="relative mx-auto flex h-16 max-w-6xl items-center gap-3 rounded-full border border-site-border bg-site-bg/85 pr-2 pl-5 shadow-site-md backdrop-blur-md @3xl:gap-6 @3xl:pr-2.5 @3xl:pl-7">
          <Brand ctx={ctx} href={home} />
          {links.length > 0 ? (
            <nav aria-label="Navigasi utama" className="ml-auto hidden @3xl:block">
              <NavLinks ctx={ctx} links={links} className="flex items-center gap-0.5" linkClassName={cx(desktopLink, "rounded-full hover:bg-site-text/5")} />
            </nav>
          ) : null}
          <div className={cx("flex items-center gap-2", links.length === 0 ? "ml-auto" : "ml-auto @3xl:ml-0")}>
            {cta ? <CtaPill ctx={ctx} cta={cta} className={links.length > 0 ? "hidden @sm:inline-flex" : undefined} /> : null}
            <MobileMenu ctx={ctx} links={links} cta={cta} floating />
          </div>
        </div>
      </SectionShell>
    );
  }

  if (node.variant === "centered") {
    return (
      <SectionShell ctx={ctx} node={node} as="header" padded={false} className="baze-navbar sticky top-(--baze-sticky-top,0px) z-40 border-b border-site-border bg-site-bg">
        <Container className="grid h-16 grid-cols-[1fr_auto_1fr] items-center gap-3 @3xl:h-20">
          <div className="flex items-center">
            <MobileMenu ctx={ctx} links={links} cta={cta} />
          </div>
          <Brand ctx={ctx} href={home} />
          <div className="flex items-center justify-end">{cta ? <CtaPill ctx={ctx} cta={cta} className={links.length > 0 ? "hidden @sm:inline-flex" : undefined} /> : null}</div>
        </Container>
        {links.length > 0 ? (
          <nav aria-label="Navigasi utama" className="hidden border-t border-site-border @3xl:block">
            <Container>
              <NavLinks ctx={ctx} links={links} className="flex h-12 items-center justify-center gap-2" linkClassName={desktopLink} />
            </Container>
          </nav>
        ) : null}
        <Progress />
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node} as="header" padded={false} className="baze-navbar sticky top-(--baze-sticky-top,0px) z-40 border-b border-site-border bg-site-bg">
      <Container className="flex h-16 items-center gap-3 @3xl:h-20 @3xl:gap-6">
        <Brand ctx={ctx} href={home} />
        {links.length > 0 ? (
          <nav aria-label="Navigasi utama" className="ml-auto hidden @3xl:block">
            <NavLinks ctx={ctx} links={links} className="flex items-center gap-1" linkClassName={desktopLink} />
          </nav>
        ) : null}
        <div className={cx("flex items-center gap-2", links.length === 0 ? "ml-auto" : "ml-auto @3xl:ml-0")}>
          {cta ? <CtaPill ctx={ctx} cta={cta} className={links.length > 0 ? "hidden @sm:inline-flex" : undefined} /> : null}
          <MobileMenu ctx={ctx} links={links} cta={cta} />
        </div>
      </Container>
      <Progress />
    </SectionShell>
  );
}
